import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import type { DeadlineFilter } from "@egalregal/shared";
import { CheckIcon, ChevronDownIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { useDemoMode } from "../demo/useDemoMode";

/** The items block's single action line (container-view 03). */
interface ItemMenuProps {
  deadlineView: boolean;
  onToggleDeadlineView: (enabled: boolean) => void;
  deadlineFilters: readonly DeadlineFilter[];
  onDeadlineFilters: (next: DeadlineFilter[]) => void;
  bulkMode: boolean;
  /** Bulk selection needs the source entry; without one there is no Select. */
  canSelect: boolean;
  onToggleBulkMode: () => void;
}

/**
 * The items row's right-aligned `More ▾` menu. Everything the old permanent
 * toolbar carried lives here in two groups — View (deadline column and both
 * deadline filters) and Items (Select / Cancel selection) — mirroring the
 * containers row's menu. Row behaviour stays in `ItemList`.
 */
export function ItemMenu({
  deadlineView,
  onToggleDeadlineView,
  deadlineFilters,
  onDeadlineFilters,
  bulkMode,
  canSelect,
  onToggleBulkMode,
}: ItemMenuProps) {
  const t = useT();
  const readOnly = useDemoMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = deadlineView || deadlineFilters.length > 0 || bulkMode;

  const close = () => setAnchor(null);

  const check = (
    key: string,
    label: string,
    checked: boolean,
    onToggle: () => void,
    disabled = false,
  ) => (
    <MenuItem key={key} disabled={disabled} onClick={onToggle}>
      <Checkbox
        edge="start"
        size="small"
        checked={checked}
        disabled={disabled}
        tabIndex={-1}
        disableRipple
        inputProps={{ "aria-label": label }}
        sx={{ ml: -1, mr: 0.5 }}
      />
      <ListItemText primary={label} />
    </MenuItem>
  );

  const heading = (key: string, label: string) => (
    <ListSubheader key={key} disableSticky sx={{ lineHeight: "32px" }}>
      {label}
    </ListSubheader>
  );

  const toggleFilter = (filter: DeadlineFilter) =>
    onDeadlineFilters(
      deadlineFilters.includes(filter)
        ? deadlineFilters.filter((entry) => entry !== filter)
        : [...deadlineFilters, filter],
    );

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        color={active ? "primary" : "inherit"}
        endIcon={<ChevronDownIcon fontSize="small" />}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
        data-testid="items-more"
        sx={{ minHeight: 40, fontWeight: 600 }}
      >
        {t("more")}
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
        {heading("view", t("view"))}
        {check("column", t("deadlines"), deadlineView, () =>
          onToggleDeadlineView(!deadlineView),
        )}
        {check(
          "overdue",
          t("deadlineOverdue"),
          deadlineFilters.includes("overdue"),
          () => toggleFilter("overdue"),
          !deadlineView,
        )}
        {check(
          "soon",
          t("deadlineSoon"),
          deadlineFilters.includes("soon"),
          () => toggleFilter("soon"),
          !deadlineView,
        )}
        {!readOnly && (
          <>
            <Divider key="items-divider" />
            {heading("items", t("items"))}
            <MenuItem
              key="select"
              data-testid="item-bulk-toggle"
              disabled={!canSelect}
              onClick={() => {
                close();
                onToggleBulkMode();
              }}
            >
              {bulkMode ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
              <ListItemText
                primary={bulkMode ? t("cancelSelectionMode") : t("bulkEdit")}
                sx={{ ml: 0.5 }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </Stack>
  );
}
